import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Check, CreditCard, TrendingUp } from 'lucide-react';
import MarketingNav from '@/components/MarketingNav';
import Footer from '@/components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Pricing = () => {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      setIsAuthenticated(true);
    } catch (_e) {
      setIsAuthenticated(false);
    }
  }, []);

  const fetchPricing = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pricing`);
      setPricing(response.data);
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPricing();
    checkAuth();
  }, [fetchPricing, checkAuth]);

  const handleBuyCard = () => {
    if (!isAuthenticated) navigate('/signup');
    else navigate('/checkout?type=physical_card');
  };
  const handleRenewSubscription = () => {
    if (!isAuthenticated) navigate('/login');
    else navigate('/checkout?type=subscription');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F1EA]">
        <div className="w-12 h-12 border-4 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F1EA] text-[#0A0A0A]" data-testid="pricing-page">
      <MarketingNav />

      <section className="pt-32 lg:pt-40 pb-16 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm font-semibold text-[#B8935A] tracking-wider uppercase mb-3">Pricing</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-bold tracking-tight text-[#0A0A0A] leading-[0.95] mb-6">
            Simple. Transparent.
          </h1>
          <p className="text-lg text-[#525252] max-w-xl mx-auto">
            One physical NFC card, a live digital profile, and everything you need to grow your network.
          </p>
        </div>
      </section>

      <section className="pb-24 px-6">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
          {pricing?.physical_card && (
            <div className="relative bg-[#0A0A0A] text-white rounded-3xl p-8 lg:p-10 shadow-2xl shadow-black/20" data-testid="physical-card-plan">
              <span className="absolute -top-3 left-8 bg-[#B8935A] text-black text-xs uppercase tracking-widest font-semibold px-3 py-1 rounded-full">
                Most Popular
              </span>
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-6">
                <CreditCard className="w-6 h-6 text-[#B8935A]" />
              </div>
              <h3 className="text-2xl font-heading font-bold tracking-tight mb-1">{pricing.physical_card.name}</h3>
              <p className="text-white/60 text-sm mb-8">{pricing.physical_card.description}</p>
              <div className="mb-8">
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-heading font-bold">₹{pricing.physical_card.price}</span>
                  <span className="text-white/50 text-sm">one-time</span>
                </div>
                <p className="text-sm text-white/50 mt-2">+ ₹365/year after your first year</p>
              </div>
              <ul className="space-y-3 mb-10">
                {pricing.physical_card.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3 text-white/85" data-testid={`feature-physical-${index}`}>
                    <Check className="w-4 h-4 text-[#B8935A] flex-shrink-0 mt-1" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={handleBuyCard}
                className="w-full bg-[#B8935A] hover:bg-[#a3824c] text-black rounded-full h-12 font-semibold"
                data-testid="buy-card-button"
              >
                Get your card
              </Button>
            </div>
          )}

          {pricing?.subscription && (
            <div className="bg-white text-[#0A0A0A] rounded-3xl p-8 lg:p-10 border border-[#0A0A0A]/10" data-testid="subscription-plan">
              <div className="w-12 h-12 rounded-xl bg-[#0A0A0A]/5 flex items-center justify-center mb-6">
                <TrendingUp className="w-6 h-6 text-[#0A0A0A]" />
              </div>
              <h3 className="text-2xl font-heading font-bold tracking-tight mb-1">{pricing.subscription.name}</h3>
              <p className="text-[#525252] text-sm mb-8">{pricing.subscription.description}</p>
              <div className="mb-8">
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-heading font-bold">₹{pricing.subscription.price}</span>
                  <span className="text-[#525252] text-sm">/year</span>
                </div>
              </div>
              <ul className="space-y-3 mb-10">
                {pricing.subscription.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3 text-[#0A0A0A]" data-testid={`feature-subscription-${index}`}>
                    <Check className="w-4 h-4 text-[#0A0A0A] flex-shrink-0 mt-1" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={handleRenewSubscription}
                variant="outline"
                className="w-full bg-transparent border-[#0A0A0A]/20 text-[#0A0A0A] hover:bg-[#0A0A0A]/5 rounded-full h-12 font-semibold"
                data-testid="renew-subscription-button"
              >
                Renew subscription
              </Button>
            </div>
          )}
        </div>

        <div className="max-w-3xl mx-auto mt-16 text-center">
          <p className="text-sm text-[#525252]">
            Looking for team pricing?{' '}
            <a href="/corporate" className="text-[#0A0A0A] font-semibold hover:underline">
              See Sol for Teams
            </a>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pricing;
